import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { ChevronLeft, ChevronRight, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { MathJax } from 'better-react-mathjax';
import * as api from '../utils/api';
import { answersMatch } from '../utils/mathUtils';

interface Question {
  id: string;
  type: 'mcq' | 'short-answer';
  question: string;
  options?: string[];
  correctAnswer?: {
    answer: string;
    explanation: string;
  };
  points: number;
}

interface Quiz {
  id: string;
  title: string;
  courseId: string;
  description: string;
  timeLimit: number;
  questions: Question[];
}

export default function QuizTakingPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Handle navigation redirects
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  useEffect(() => {
    loadQuiz();
  }, [quizId, user]);

  useEffect(() => {
    if (!quizStarted) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [quizStarted, startTime]);

  const loadQuiz = async () => {
    try {
      setLoading(true);

      if (!quizId || !user) return;

      // Check if already submitted
      const existingAttempt = await api.getSubmissionByQuiz(quizId);
      
      if (existingAttempt) {
        toast.error('You have already completed this quiz');
        setRedirectTo(`/quiz/${quizId}/summary`);
        return;
      }

      // Load quiz data
      const quizData = await api.getQuizById(quizId);
      
      if (!quizData) {
        throw new Error('Quiz not found');
      }

      setQuiz(quizData);
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error('Failed to load quiz');
      setRedirectTo('/student');
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setStartTime(Date.now());
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (quiz && currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleQuestionJump = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const handleSubmit = async () => {
    if (!quiz || !user) return;

    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      if (!confirm(`You have ${unanswered.length} unanswered question(s). Submit anyway?`)) {
        return;
      }
    }

    if (!confirm('Are you sure you want to submit this quiz? You cannot change your answers after submission.')) {
      return;
    }

    try {
      setSubmitting(true);

      const duration = Math.floor((Date.now() - startTime) / 1000);
      
      // Calculate score
      let score = 0;
      let totalPoints = 0;

      quiz.questions.forEach(question => {
        totalPoints += question.points;
        const userAnswer = answers[question.id] || '';

        if (answersMatch(userAnswer, question.correctAnswer?.answer || '')) {
          score += question.points;
        }
      });

      // Submit quiz to API
      await api.submitQuiz({
        quizId: quiz.id,
        answers,
        timeSpent: duration
      });

      toast.success('Quiz submitted successfully!');
      setRedirectTo(`/quiz/${quiz.id}/summary`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Quiz not found</p>
      </div>
    );
  }

  if (!quizStarted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{quiz.title}</CardTitle>
            <p className="text-gray-600 mt-2">{quiz.description}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Quiz Information</h3>
              <div className="space-y-2 text-gray-700">
                <p>• Total Questions: {quiz.questions.length}</p>
                <p>• Time Limit: {quiz.timeLimit} minutes</p>
                <p>• Question Types: Multiple Choice & Short Answer</p>
                <p>• Timer will start when you begin</p>
                <p>• You can navigate between questions</p>
                <p>• Review your answers before submission</p>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> You can only submit this quiz once. Make sure to review all your answers before submitting.
              </p>
            </div>

            <Button onClick={handleStartQuiz} className="w-full" size="lg">
              Start Quiz
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-5xl mx-auto py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl text-gray-900">{quiz.title}</h2>
              <p className="text-sm text-gray-600">{quiz.description}</p>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="w-5 h-5" />
              <span className="font-mono">{formatTime(elapsedTime)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Question {currentQuestionIndex + 1} of {quiz.questions.length}</span>
              <span>{progress.toFixed(0)}% Complete</span>
            </div>
            <Progress value={progress} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Navigation */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 lg:grid-cols-4 gap-2">
                  {quiz.questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => handleQuestionJump(index)}
                      className={`
                        aspect-square rounded-md text-sm font-medium transition-colors
                        ${index === currentQuestionIndex
                          ? 'bg-blue-600 text-white'
                          : answers[q.id]
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Question {currentQuestionIndex + 1}</CardTitle>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {currentQuestion.points} points
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Question Text */}
                <div className="prose max-w-none">
                  <MathJax dynamic>{currentQuestion.question}</MathJax>
                </div>

                {/* Answer Input */}
                {currentQuestion.type === 'mcq' && currentQuestion.options ? (
                  <div className="space-y-3">
                    {currentQuestion.options.map((choice, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerChange(currentQuestion.id, choice)}
                        className={`
                          w-full text-left p-4 rounded-lg border-2 transition-colors
                          ${answers[currentQuestion.id] === choice
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <MathJax dynamic>{choice}</MathJax>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div>
                    <Textarea
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      placeholder="Type your answer here..."
                      className="min-h-[200px]"
                    />
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    variant="outline"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>

                  {currentQuestionIndex === quiz.questions.length - 1 ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? 'Submitting...' : 'Submit Quiz'}
                    </Button>
                  ) : (
                    <Button onClick={handleNext}>
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}